import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  refreshCsrfToken,
  clearCsrfToken,
  decodeJwtPayload,
} from '../../../gen/client/react'
import { setApiToken, getApiToken, client } from '../api/http'
import { createPKCE, randomState } from './pkce'
import { clearPersistedActiveTenant } from './activeTenantStorage'

/**
 * Build-time client ID injected by webpack DefinePlugin.
 * Falls back to 'oidc-admin-ui' when the env var is absent (local dev / tests).
 */
const CLIENT_ID = process.env.THREADID_UI_CLIENT_ID || 'oidc-admin-ui'

/**
 * localStorage key under which the access token is persisted.
 *
 * Security note: localStorage is shared across all same-origin tabs/windows and
 * persists until explicitly cleared (unlike sessionStorage which is tab-scoped).
 * It is readable by any script running on the same origin (XSS tradeoff).
 * We choose localStorage over sessionStorage so the user remains authenticated
 * across tabs and browser refreshes without requiring a fresh PKCE flow in every
 * window. The token is short-lived and the server re-verifies on every request —
 * this persistence is purely for UX continuity. Explicit logout or token expiry
 * clears it immediately.
 *
 * PKCE per-tab state (pkce_verifier, oauth_state) remains in sessionStorage —
 * those are correctly per-tab for the in-flight login and must not be shared.
 */
export const SESSION_TOKEN_KEY = 'admin_access_token'

/** sessionStorage key holding the path (pathname+search) the user was on when
 *  login() redirected them to /authorize — Callback reads and clears it to
 *  send the user back to their original destination instead of a fixed page. */
export const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect'

/**
 * JWT claims decoded from the access token (display-only; never verified
 * client-side). This is the PRE-WHOAMI BOOTSTRAP ONLY: it is what renders
 * before the server-authoritative `adminWhoAmI` call (see `permissions` /
 * `adminRole` below) resolves, and it remains the source for the coarse
 * `requireAdmin` (roles.includes('admin')) check ProtectedRoute already
 * performed before Task 9. It must never be used as the source of truth for
 * fine-grained permission decisions — those are driven exclusively by the
 * server-reported `permissions` array.
 */
export interface AdminClaims {
  sub: string
  roles: string[]
  [k: string]: unknown
}

/**
 * Explicit resolution state for `permissions`/`adminRole`, mirroring
 * TenantSwitcher's `TenantResolutionStatus` pattern (src/layout/
 * TenantSwitcher.tsx) so "not authenticated yet" is never conflated with
 * "the whoami fetch failed" — the same conflation that previously produced a
 * perpetual-spinner class of bug for tenant resolution (fix-wave
 * 2026-07-24, commits 291c525f/df742ee2). Consumers (ProtectedRoute,
 * AdminLayout) must treat 'idle'/'loading' as "not yet known" (show a
 * loading state, never a denial) and 'error' as a distinct, retryable state
 * (never silently treated as either "allow everything" or "deny
 * everything").
 *
 *   idle     — no authenticated token yet, so no fetch has been attempted.
 *   loading  — a token is present and adminWhoAmI is in flight.
 *   resolved — the server responded; `permissions`/`adminRole` are current.
 *   error    — the fetch failed. Stale-but-previously-resolved permissions
 *              are deliberately NOT cleared (see fetchPermissions below) so
 *              a transient refetch failure can never un-authorize an
 *              already-authorized admin mid-session.
 */
export type PermissionsStatus = 'idle' | 'loading' | 'resolved' | 'error'

export interface AuthContextValue {
  token: string | null
  claims: AdminClaims | null
  /**
   * Server-resolved `admin_role` from GET /v1/admin/whoami — authoritative,
   * unlike the same-named unverified JWT claim: it is re-derived live by the
   * server on every whoami call via the identical resolvePermissionSet path
   * enforcePermission uses, so it can never drift from what admin routes
   * actually enforce. `null` means either "not yet resolved" (see
   * `permissionsStatus`) or a genuinely unscoped/backward-compat admin.
   */
  adminRole: string | null
  /**
   * Server-resolved EFFECTIVE permission set from GET /v1/admin/whoami.
   * `null` until resolved — see `permissionsStatus` for the explicit state;
   * never infer "unresolved" from an empty array (a resolved-but-empty set
   * is a real, distinct outcome — e.g. a stale custom role).
   */
  permissions: string[] | null
  /** Explicit resolution state for `permissions`/`adminRole` — see
   *  `PermissionsStatus` above. */
  permissionsStatus: PermissionsStatus
  /**
   * Re-fetch permissions on demand (e.g. a "Retry" action after
   * `permissionsStatus === 'error'`). Also called automatically whenever
   * `token` transitions to a non-null value (rehydration, fresh login, or
   * cross-tab sync) — most callers never need to invoke this directly.
   */
  refreshPermissions(): Promise<void>
  /** Initiate PKCE authorization-code login flow. Pass { acrValues } to
   *  request a step-up ceremony (e.g. 'pam:aal3') instead of a plain login. */
  login(opts?: { acrValues?: string }): void
  /**
   * Soft-clear: remove token and claims from memory and localStorage without
   * destroying the server session. Called by the API client on 401 so the
   * in-app ProtectedRoute sign-in card is shown in the same window. The user
   * can re-authenticate via a silent SSO round-trip if the server session is
   * still valid (no new password required). Does NOT navigate.
   */
  clearAuth(): void
  /**
   * Hard-clear + redirect: clear token/claims, then navigate to /logout to
   * destroy the server session and land on the signed-out home page.
   * Use for the explicit Logout button — not for transient 401 handling.
   */
  logout(): void
  /**
   * Exchange the authorization code from the callback URL for an access token,
   * then decode (unverified) claims and store them in React state.
   */
  handleCallback(params: URLSearchParams): Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Rehydrate from localStorage on first mount so an already-authenticated
  // user is recognised after an OAuth callback redirect, a browser refresh,
  // or when opening a new same-origin tab (localStorage is shared).
  //
  // The lazy initializers run synchronously before the first render; calling
  // setApiToken here primes the bearer token before any component can fire an
  // authenticated request. decodeJwtPayload is UNVERIFIED: UI-gating only.
  const [token, setToken] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(SESSION_TOKEN_KEY)
      if (!stored) return null
      const decoded = decodeJwtPayload(stored)
      if (!decoded) {
        localStorage.removeItem(SESSION_TOKEN_KEY)
        return null
      }
      // Treat an expired token as logged-out so ProtectedRoute shows the
      // sign-in prompt rather than bouncing the user on the first API call.
      if (typeof decoded.exp === 'number' && decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem(SESSION_TOKEN_KEY)
        return null
      }
      // Prime the API bearer token immediately so the very first request after
      // reload is already authenticated (before any useEffect runs).
      setApiToken(stored)
      return stored
    } catch {
      return null
    }
  })

  const [claims, setClaims] = useState<AdminClaims | null>(() => {
    try {
      // If the token initializer above removed an expired/invalid entry, this
      // call returns null because the item is already gone.
      const stored = localStorage.getItem(SESSION_TOKEN_KEY)
      if (!stored) return null
      const decoded = decodeJwtPayload(stored)
      if (!decoded) return null
      if (typeof decoded.exp === 'number' && decoded.exp * 1000 < Date.now()) {
        return null
      }
      return decoded as AdminClaims
    } catch {
      return null
    }
  })

  // Server-authoritative admin_role/permissions (GET /v1/admin/whoami) — see
  // the AuthContextValue doc comments above. Starts unresolved even when a
  // token rehydrates synchronously above; the fetch-on-token-change effect
  // below (not a lazy initializer) is what resolves it, since it requires an
  // async round-trip the lazy initializers here cannot perform.
  const [adminRole, setAdminRole] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<string[] | null>(null)
  const [permissionsStatus, setPermissionsStatus] =
    useState<PermissionsStatus>('idle')

  /**
   * Soft-clear auth state: remove token and claims from memory and localStorage
   * without touching the server session. A transient or expired-token 401 from
   * the API calls this so the user sees the in-app ProtectedRoute sign-in card
   * in the same window instead of being hard-redirected to /logout.
   *
   * Does NOT call window.location.assign — use logout() for that.
   *
   * Declared above fetchPermissions (rather than in its usual place near
   * login/logout) because fetchPermissions' 401 handling (Finding 2, fix-wave
   * 2026-07-24) needs to call it and function const declarations cannot be
   * referenced before their point of definition.
   */
  const clearAuth = useCallback((): void => {
    setToken(null)
    setClaims(null)
    setApiToken(null)
    clearCsrfToken()
    localStorage.removeItem(SESSION_TOKEN_KEY)
  }, [])

  // Fetches the caller's EFFECTIVE authorization from the server — the same
  // resolvePermissionSet path enforcePermission uses on every gated admin
  // route (see admin_whoami_keys_server.go) — so the UI's permission
  // decisions can never drift from what the server actually enforces.
  //
  // On failure, a PREVIOUSLY resolved permissions/adminRole value is
  // deliberately left untouched (status still moves to 'error' so a fresh
  // caller — one who has never resolved — sees the distinct error state, but
  // an already-authorized admin's UI does not silently downgrade to "no
  // permissions" on a transient refetch failure). This mirrors the
  // no-silent-fail-open-or-fail-closed-mid-session requirement from the
  // 2026-07-24 fix wave's tenant-resolution bug (commits 291c525f/df742ee2).
  //
  // Finding 1 (CRITICAL, fix-wave 2026-07-24): GET /v1/admin/whoami is
  // spec'd `auth: "none"` — deliberately, like every other /v1/admin/* route
  // (see ui/src/api/genClient.ts) — so the generated client's applyHTTPAuth
  // is a no-op here and never attaches a bearer token on its own. Every other
  // admin caller in this app goes through useAdminGenClient(), which injects
  // the token via `config.headers` on a client instance recreated per-token.
  // The singleton `client` here (ui/src/api/http.ts) is created ONCE at
  // module load with a static config, so it cannot be recreated per-token the
  // same way; instead the token is attached per-call via RequestOptions.headers
  // (merged onto every request — see ApicClient.request in
  // gen/client/react/client.ts), read fresh from getApiToken() on each call so
  // it can never go stale.
  //
  // Finding 2 (fix-wave 2026-07-24): a 401 here means the session is dead
  // (expired/revoked token) — the same condition useAdminGenClient's
  // onUnauthorized handles by calling clearAuth() so ProtectedRoute shows the
  // sign-in card and the user can re-authenticate. Routing whoami's 401
  // through the identical path avoids stranding the user on a Retry button
  // that can never succeed (the retried request would carry the same dead
  // token and 401 again). Any other failure (network error, 5xx, etc.) is
  // NOT a dead session — it stays in the existing retryable 'error' state.
  //
  // Checked via duck-typed `.status` (matching how genClient.test.tsx already
  // asserts on captured errors) rather than `instanceof HTTPError`: several
  // existing test suites mock '@gen/react'/'../api/http' with factories that
  // don't re-export the HTTPError class, which would make `instanceof`
  // itself throw ("Right-hand side of 'instanceof' is not an object") for
  // every whoami failure in those suites, well beyond this one code path.
  const fetchPermissions = useCallback(async (): Promise<void> => {
    setPermissionsStatus((prev) => (prev === 'resolved' ? prev : 'loading'))
    try {
      const bearerToken = getApiToken()
      const resp = await client.adminWhoAmI(
        {},
        bearerToken
          ? { headers: { Authorization: `Bearer ${bearerToken}` } }
          : undefined
      )
      setPermissions(resp.permissions ?? [])
      setAdminRole(resp.adminRole ?? null)
      setPermissionsStatus('resolved')
    } catch (err) {
      const status = (err as { status?: unknown } | null | undefined)?.status
      if (status === 401) {
        clearAuth()
        return
      }
      setPermissionsStatus((prev) => (prev === 'resolved' ? prev : 'error'))
    }
  }, [clearAuth])

  // Single source of truth for WHEN permissions are (re)fetched: any time
  // `token` transitions to a non-null value — covering fresh login
  // (handleCallback), localStorage rehydration, and cross-tab sync
  // (the 'storage' listener below) uniformly, without needing a separate
  // call site wired into each of those three paths. A token becoming null
  // (logout/clearAuth) resets to the unresolved 'idle' state rather than
  // leaving a stale permission set behind for whichever caller signs in next
  // in the same tab.
  useEffect(() => {
    if (!token) {
      setPermissions(null)
      setAdminRole(null)
      setPermissionsStatus('idle')
      return
    }
    fetchPermissions()
  }, [token, fetchPermissions])

  // Re-prime the CSRF cookie once on mount when we rehydrated a token from
  // localStorage. We read from localStorage directly (not from the token
  // state variable) to avoid a react-hooks/exhaustive-deps violation on a
  // reactive value in a mount-only effect.
  //
  // A failed re-prime (network down or token expired server-side) should not
  // crash the app — the user will see a 403 on the next API call and can
  // re-authenticate from there.
  useEffect(() => {
    if (localStorage.getItem(SESSION_TOKEN_KEY) !== null) {
      refreshCsrfToken().catch(() => {
        // Intentional no-op: see comment above.
      })
    }
  }, []) // mount-only

  // Cross-tab auth sync: when another same-origin tab removes or sets the
  // token key in localStorage (e.g. logout or a fresh login), mirror that
  // state here so all tabs stay in sync. The browser fires 'storage' events
  // in every tab EXCEPT the one that made the write — so the originating tab
  // updates its own state through the normal React state setters, and all
  // other tabs react here.
  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent): void => {
      if (event.key !== SESSION_TOKEN_KEY) return
      if (event.newValue === null) {
        // Token removed (logout in another tab) — clear this tab's state.
        setToken(null)
        setClaims(null)
        setApiToken(null)
        clearCsrfToken()
      } else {
        // Token set/updated (login in another tab) — rehydrate this tab.
        const decoded = decodeJwtPayload(event.newValue)
        if (!decoded) return
        if (
          typeof decoded.exp === 'number' &&
          decoded.exp * 1000 < Date.now()
        ) {
          return
        }
        setToken(event.newValue)
        setClaims(decoded as AdminClaims)
        setApiToken(event.newValue)
      }
    }

    window.addEventListener('storage', handleStorageEvent)
    return () => {
      window.removeEventListener('storage', handleStorageEvent)
    }
  }, []) // mount-only

  /**
   * Generate PKCE verifier+challenge, persist them in sessionStorage, then
   * redirect the browser to the /authorize endpoint.
   *
   * Returns void (async work is fire-and-forget) so the interface signature
   * stays `login(opts?: { acrValues?: string }): void`.
   */
  const login = useCallback((opts?: { acrValues?: string }): void => {
    createPKCE().then(({ verifier, challenge }) => {
      const state = randomState()
      sessionStorage.setItem('pkce_verifier', verifier)
      sessionStorage.setItem('oauth_state', state)
      // Stash the deep link the user was actually trying to reach (e.g. a PAM
      // access-request detail page) so Callback can return them there instead
      // of always landing on the /admin index route (-> pam-overview). Only
      // the path+search is needed (same-origin, no token/PII in it).
      sessionStorage.setItem(
        POST_LOGIN_REDIRECT_KEY,
        window.location.pathname + window.location.search
      )
      const redirectUri = `${window.location.origin}/admin/callback`
      let url =
        '/authorize?response_type=code' +
        `&client_id=${encodeURIComponent(CLIENT_ID)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        '&scope=openid' +
        `&code_challenge=${encodeURIComponent(challenge)}` +
        '&code_challenge_method=S256' +
        `&state=${encodeURIComponent(state)}`
      if (opts?.acrValues) {
        url += `&acr_values=${encodeURIComponent(opts.acrValues)}`
      }
      window.location.assign(url)
    })
  }, [])

  const logout = useCallback((): void => {
    setToken(null)
    setClaims(null)
    setApiToken(null)
    clearCsrfToken()
    sessionStorage.removeItem('pkce_verifier')
    sessionStorage.removeItem('oauth_state')
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
    localStorage.removeItem(SESSION_TOKEN_KEY)
    // Finding 3 (fix-wave 2026-07-24, CRITICAL): the persisted active-tenant
    // selection (src/auth/activeTenantStorage.ts, written by TenantProvider)
    // must be cleared here too. Without this, on a shared browser the NEXT
    // user to authenticate could silently inherit this user's tenant
    // selection — subject-scoping the persisted value (see
    // activeTenantStorage.ts) already prevents a DIFFERENT subject from ever
    // reading it back, but clearing it outright on logout is defense in
    // depth and good hygiene regardless.
    clearPersistedActiveTenant()
    // Destroy the server session + clear the oidc_session cookie, then land on
    // the (logged-out) home page. A full navigation guarantees both the server
    // session and all client state are gone.
    window.location.assign('/logout')
  }, [])

  const handleCallback = useCallback(
    async (params: URLSearchParams): Promise<void> => {
      // Validate state to prevent CSRF
      const returnedState = params.get('state')
      const storedState = sessionStorage.getItem('oauth_state')
      if (!returnedState || returnedState !== storedState) {
        throw new Error('OAuth state mismatch — possible CSRF')
      }

      const authorizationError = params.get('error')
      if (authorizationError) {
        throw new Error(
          authorizationError === 'access_denied'
            ? 'Authorization was denied'
            : 'Authorization failed'
        )
      }

      const code = params.get('code')
      if (!code) throw new Error('Missing authorization code')

      const verifier = sessionStorage.getItem('pkce_verifier')
      if (!verifier) throw new Error('Missing PKCE verifier')

      const redirectUri = `${window.location.origin}/admin/callback`
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: CLIENT_ID,
        code_verifier: verifier,
      })

      // The URL is a hard-coded same-origin path; OAuth values are confined to
      // the POST body and cannot influence the request destination.
      // gitlab-advanced-sast-exclude
      const res = await fetch('/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })

      if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`)

      const data = (await res.json()) as { access_token?: unknown }
      if (
        typeof data.access_token !== 'string' ||
        data.access_token.length === 0
      ) {
        throw new Error('Token exchange returned no access token')
      }
      const accessToken = data.access_token

      // Decode before committing any client auth state. A malformed token must
      // not become the bearer token merely because the exchange returned 200.
      // decodeJwtPayload is UNVERIFIED and remains UI-gating only; the server
      // re-verifies the signed token on every API request.
      const decoded = decodeJwtPayload(accessToken)
      if (!decoded) throw new Error('Failed to decode JWT payload')

      setToken(accessToken)
      setApiToken(accessToken)
      setClaims(decoded as AdminClaims)

      // Persist the access token so the user remains authenticated across the
      // OAuth callback redirect, any subsequent browser refreshes, and across
      // all same-origin tabs/windows.
      localStorage.setItem(SESSION_TOKEN_KEY, accessToken)

      sessionStorage.removeItem('pkce_verifier')
      sessionStorage.removeItem('oauth_state')

      // CSRF priming is best-effort and MUST NOT hold the OAuth callback on an
      // indefinite loading screen after the access token is already stored.
      // The generated cookie-auth client can refresh and retry a missing CSRF
      // token on the first unsafe request; bearer-authenticated reads can begin
      // immediately. A browser refresh used to appear to "fix" this because
      // the token had been persisted before this await.
      refreshCsrfToken().catch(() => {
        // Intentional no-op: see the comment above.
      })
    },
    []
  )

  const contextValue = useMemo(
    () => ({
      token,
      claims,
      adminRole,
      permissions,
      permissionsStatus,
      refreshPermissions: fetchPermissions,
      login,
      clearAuth,
      logout,
      handleCallback,
    }),
    [
      token,
      claims,
      adminRole,
      permissions,
      permissionsStatus,
      fetchPermissions,
      login,
      clearAuth,
      logout,
      handleCallback,
    ]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
